import {html, HTMLTemplateResult, nothing} from "lit";
import {AbstractO2dElement} from "@o2d/shared";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {styleMap} from "lit/directives/style-map.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";
import {translate} from "lit-translate";
import {container} from "tsyringe";
import {Subject, from} from "rxjs";
import {switchMap, takeUntil} from "rxjs/operators";

import {RxjsLifecycleController} from "../_reactive-controllers/rxjs-lifecycle.controller";
import {JustMovedFlashController} from "../_reactive-controllers/just-moved-flash.controller";
import {DissolveFlashController} from "../_reactive-controllers/dissolve-flash.controller";
import {EntityLiveRefreshController} from "../_reactive-controllers/entity-live-refresh.controller";
import {IssueRepository} from "../../domain/repositories/issue.repository";
import {IssuePersonalDataRepository} from "../../domain/repositories/issue-personal-data.repository";
import {EmployeeToIssueAssignmentRepository} from "../../domain/repositories/employee-to-issue-assignment.repository";
import {CommandBus} from "../../services/command-bus.service";
import {UnassignIssueFromPersonalLabelCommand} from "../../commands/unassign-issue-from-personal-label.command";
import {UpdateIssuePersonalDataCommand} from "../../commands/update-issue-personal-data.command";
import {IssueHelper} from "../../domain/helpers/issue.helper";
import {Issue} from "../../domain/entities/issue.entity";
import {appState} from "../../app.state";
import {UiHelper} from "../../helpers/ui.helper";
import {
    MY_ORGANIZER_ISSUE_LABELED_EVENT,
    MY_ORGANIZER_ISSUE_UNLABELED_EVENT,
    MY_ORGANIZER_ISSUE_DATED_EVENT,
    MY_ORGANIZER_ISSUE_UNDATED_EVENT,
    MyOrganizerDragContext,
    type MyOrganizerIssueLabeledEventDetail,
    type MyOrganizerIssueUnlabeledEventDetail,
    type MyOrganizerIssueDatedEventDetail,
    type MyOrganizerIssueUndatedEventDetail,
} from "./my-organizer-drag-context";
import {rootUidOf} from "../../domain/helpers/issue-type-root.helper";

const DATE_FORMAT = {day: `2-digit`, month: `2-digit`, year: `numeric`} as const;

/**
 * "My Organizer" mode's first column (docs/feature-plans/0026-personal-
 * labels-issue-data/, follow-up UI step, Anke 2026-09-06: "so ähnlich wie
 * tasklist"): every Issue assigned to the current User that is not done — no
 * milestones, absences, holidays or meetings (D23) — see
 * `IssueRepository.findAllAssignedToMe()`.
 *
 * Deliberately the SIMPLE half of `<ha-task-list-item>` (which shows the
 * full card — description, assignees, project, keywords, creator): only
 * status, date and title per row, per explicit request ("es sollen aber nur
 * folgende Angaben pro Issue angezeigt werden"). No virtualizer either — a
 * User's own open/reminder assignments are not expected to be thousands of
 * rows the way the full Tasklist can be.
 *
 * An Issue belongs to exactly ONE column of THIS page's own organizing
 * dimension at a time (Anke, 2026-09-06: "ein issue kann nur genau einer
 * Spalte zugewiesen werden") — once dropped into a `<my-organizer-label-
 * column>`, it is excluded from here — both live (via the
 * `MY_ORGANIZER_ISSUE_LABELED_EVENT` window event that column dispatches on
 * a successful drop, subscribed in `connectedCallback`) and on a fresh load
 * (`_load`, by cross-referencing `IssuePersonalDataRepository.findAllMine()`).
 * On `dimension="label"`, this inbox ALSO reacts to a ticket becoming
 * un-labeled from ELSEWHERE on the page — namely a `<my-organizer-label-
 * column>` being DELETED (Anke, 2026-09-09), which clears `personalLabel`
 * on every one of its own tickets before removing itself — see
 * `connectedCallback`'s `MY_ORGANIZER_ISSUE_UNLABELED_EVENT` listener below,
 * the exact `dimension="label"` analog of the UNDATED listener a few lines
 * further down.
 *
 * "Mein Kalender" (2026-09-07) mounts this exact same component for ITS OWN
 * inbox — Anke: "auf allen beiden seiten (kalender und boxen) sind alle
 * tickets... in irgendeiner spalte zu sehen. deshalb sage ich, dass die
 * grundmenge gleich ist. wenn ich ganz neu starte, dann ist die inbox auf
 * den beiden seiten mit den exakt gleichen tickets gefüllt. und dann kann
 * ich diese tickets einfach nur auf 2 unterschiedliche arten verteilen:
 * einmal nach datum und zusätzlich nach einem label." — i.e. "Meine Boxen"
 * and "Mein Kalender" each sort the SAME starting pool by their OWN,
 * INDEPENDENT criterion; an Issue already labeled but not yet dated must
 * still show in the CALENDAR page's inbox (it hasn't been placed on any
 * day yet), and one already dated but not yet labeled must still show in
 * the BOXEN page's inbox. `dimension` (a `@property`, set by whichever page
 * mounts this) picks which single field this instance's inbox excludes by:
 * `'label'` (the default, "Meine Boxen") checks ONLY `personalLabel`;
 * `'date'` ("Mein Kalender") checks ONLY `personalDate`. The two ARE
 * therefore the exact same starting set on a fresh board (nothing sorted
 * yet, so neither field excludes anything on EITHER page) and naturally
 * diverge from there as the User sorts each page independently — this was
 * the whole point (an earlier version of this file checked EITHER field
 * regardless of page, which wrongly hid a merely-labeled-but-not-yet-dated
 * Issue from the calendar's inbox too — a review-fund, 2026-09-07).
 *
 * This column is ALSO a drop target itself (Anke, 2026-09-06: "es soll auch
 * möglich sein, das Issue wieder zurück in die linke Spalte zu ziehen") —
 * dropping an Issue dragged out of a label column here clears its
 * `personalLabel` (see `_onDrop`), the reverse of `<my-organizer-label-
 * column>`'s own drop handler; on the calendar page (`dimension="date"`),
 * dropping one dragged out of a calendar day clears its `personalDate` the
 * same way instead, the reverse of `<my-organizer-calendar-day>`'s own drop
 * handler — `_onDrop` only ever touches the ONE field `dimension` names,
 * exactly mirroring `_load`'s own single-field exclusion.
 *
 * On `dimension="date"`, this inbox ALSO reacts to a ticket becoming
 * un-dated from ELSEWHERE on the page — namely `<o2d-personal-organizer-
 * calendar-view>`'s own stale-`personalDate` reclaim, which runs on every
 * load and clears any date that has fallen into the past (Anke, 2026-09-07:
 * "wenn die nächste Woche beginnt und es gab noch tickets in der Woche
 * zugeordnet, die dann in der Vergangenheit liegt, dann bitte das
 * zugeordnete persönliche Datum entfernen und die Tickets wieder in der
 * Inbox anzeigen") — see `connectedCallback`'s `MY_ORGANIZER_ISSUE_UNDATED_EVENT`
 * listener below.
 *
 * Extends `AbstractO2dElement` (`@o2d/shared`), not the legacy
 * `AbstractLitElement` — mandatory for new components per ADR-007. RxJS
 * lifecycle (auto-unsubscribe on disconnect) comes via the `RxjsLifecycleController`
 * Reactive Controller (Composition), not base-class inheritance — see
 * ADR-007 §2/§6 and the controller's own `listenUntilDisconnected()` doc
 * comment.
 */
@customElement('my-organizer-assigned-column')
export class MyOrganizerAssignedColumnComponent extends AbstractO2dElement {

    /** Which single field this inbox instance excludes by — see the class
     *  doc comment. `'label'` is the default so "Meine Boxen" (the ORIGINAL
     *  page this component was built for) needs no attribute at all. */
    @property({type: String})
    public dimension: 'label' | 'date' = 'label';

    @state()
    private _issues: Issue[] = [];

    @state()
    private _loaded = false;

    @state()
    private _isDropTarget = false;

    /** Re-entry guard for the "+ Aufgabe" button — a click while the create
     *  dialog is already opening must not queue a second one (mirrors
     *  `knowledge-sidebar-list.component.ts`'s own `_creating` guard). */
    private _creating = false;

    /**
     * "Pressed" and "dragging" (Anke, 2026-09-06) — see
     * `<my-organizer-label-column>`'s own doc comments on `_pressedIssueUid`/
     * `_draggingIssueUid` for the full reasoning (JS-tracked by `issue.uid`,
     * deliberately NOT the native `:active` CSS pseudo-class, which a
     * 2026-09-06 bug report traced to coloring the WRONG, uninvolved row
     * after a drop-without-moving-the-mouse — the shared row CSS class
     * carries the exact same risk here).
     */
    @state()
    private _pressedIssueUid: string | null = null;

    @state()
    private _draggingIssueUid: string | null = null;

    /**
     * "Just moved here" flash (instant appear, 2s fade-out) — see
     * `JustMovedFlashController`'s own doc comment for the full reasoning;
     * shared with `<my-organizer-label-column>`/`<my-organizer-calendar-
     * day>` instead of a private copy (review-fund, 2026-09-07).
     */
    private readonly _justMoved = new JustMovedFlashController(this);

    /** "Went DONE" dissolve animation (Anke, 2026-09-09, follow-up: "auch in
     *  der Inbox") — see `DissolveFlashController`'s own doc comment and
     *  `_applyFreshIssues()` below. */
    private readonly _dissolve = new DissolveFlashController(this);

    public readonly rxjs = new RxjsLifecycleController(this);

    /**
     * The SOLE trigger for every reload of `_issues` after the component has
     * mounted — the initial `connectedCallback()` load, the `dimension="date"`
     * UNDATED-event reaction, and the `_onDrop` failure-fallback ALL push
     * onto this `Subject` (`.next()`) rather than calling `_fetchIssues()`/
     * `_load()` directly, so `switchMap` (below) can discard a stale,
     * still-in-flight reload's result if a NEWER one starts before it
     * resolves.
     *
     * review-fund, 2026-09-07 (round 1): several `IssuePersonalData` rows
     * reclaimed in one page load previously fired several overlapping,
     * un-sequenced `_load()` calls that could race and leave `_issues`
     * reflecting an outdated server response instead of the latest one.
     *
     * review-fund, 2026-09-07 (round 2 — this one didn't go far enough): the
     * INITIAL load in `connectedCallback()` still called `_load()` directly,
     * bypassing this pipeline entirely — since `<o2d-personal-organizer-
     * calendar-view>` and this inbox mount together, the initial fetch and
     * the parent view's own stale-date reclaim (which can fire an UNDATED
     * event mid-flight) raced each other one level deeper than before. Now
     * EVERY trigger goes through this one `Subject`, so there is only ever
     * one code path that can win.
     */
    private readonly _reload$ = new Subject<void>();

    /** Bumped once per `_reload$` emission that reaches `subscribe()` — see
     *  that `subscribe()` callback's own comment for why this exists
     *  (review-fund, 2026-09-09, round 2). */
    private _reloadGeneration = 0;

    /**
     * Live-refresh (Anke, 2026-09-09 bug report: "wenn ich aus der My
     * Organizer-Ansicht auf ein Ticket klicke... den Status ändere und
     * speichere, dann ist diese Änderung nicht sofort in der My Organizer-
     * Ansicht zu sehen, sondern erst nach Neuladen. bei der Timeline ist
     * das sofort zu sehen.") — same `EntityLiveRefreshController` the
     * Timeline views and `<ha-combobox>` already use for exactly this
     * (`PRD-061`/`PRD-043`), applied here for the first time. Routes
     * through `_reload$` like every other trigger — a status edit that
     * moves an Issue onto/off DONE, or between OPEN/REMINDER, must run
     * through the SAME `findAllAssignedToMe()` re-query the rest of this
     * pipeline already does, not a local patch of the changed fields (a
     * status change can add OR remove the Issue from this list entirely,
     * which a field-level patch could never express).
     *
     * A status change TO DONE specifically gets the same dissolve animation
     * as `<my-organizer-label-column>`/`<my-organizer-calendar-day>` (Anke,
     * 2026-09-09, follow-up: "auch in der Inbox") — see
     * `_applyFreshIssues()` below, which is what actually applies this
     * pipeline's fetch result to `_issues` now.
     */
    private readonly _liveRefresh = new EntityLiveRefreshController(this, {
        getEntityClass: () => Issue,
        onReload: () => {
            this._reload$.next();
            return Promise.resolve();
        },
    });

    public override connectedCallback(): void {
        super.connectedCallback();

        // The single pipeline every reload trigger feeds into — see
        // `_reload$`'s own doc comment. Set up FIRST, unconditionally (both
        // `dimension`s), before anything below can call `.next()` on it.
        this._reload$
            .pipe(
                takeUntil(this.rxjs.disconnected$),
                switchMap(() => from(this._fetchIssues())),
            )
            .subscribe(issues => {
                // Generation counter (review-fund, 2026-09-09, round 2):
                // `switchMap` only cancels a stale IN-FLIGHT `_fetchIssues()`
                // — once a fetch's result already reached this `subscribe`,
                // `_applyFreshIssues()`'s OWN async work below (its
                // `findOneById()` round-trip for a missing Issue) is no
                // longer under `switchMap`'s control at all. Two reloads
                // whose fetches BOTH land here (not superseded by
                // `switchMap`) could otherwise finish their OWN async work
                // out of order — an older reload's slower merge landing
                // AFTER a newer, faster one, clobbering `_issues` with stale
                // data. `_applyFreshIssues` checks its own `generation`
                // against the current one right before merging and skips
                // the merge if a newer reload already started.
                const generation = ++this._reloadGeneration;
                // `.catch()`: `_applyFreshIssues()` can throw (its
                // `findOneById()` round-trip for a missing Issue can
                // reject) — without this, that rejection was an unhandled
                // promise rejection, silently dropping this ENTIRE reload's
                // result (including `_mergeFreshIssues()`, which never
                // runs) with no visible error and no retry.
                this._applyFreshIssues(issues, generation).catch((error: unknown) => {
                    console.error('[my-organizer-assigned-column] failed to apply a fresh reload', error);
                });
                this._loaded = true;
            });

        // Cross-column live sync — unsubscribes automatically on disconnect
        // via the RxjsLifecycleController, no manual `disconnectedCallback`
        // override/listener bookkeeping needed. Only the ONE event pair
        // matching `dimension` is subscribed — see the class doc comment on
        // why this inbox must not react to the other page's own dimension
        // (in practice the other page's columns aren't even mounted at the
        // same time, since the two "My Organizer" pages are separate routed
        // views, but subscribing to only what's relevant keeps that
        // invariant explicit rather than incidental).
        if (this.dimension === 'label') {
            this.rxjs.listenUntilDisconnected<CustomEvent<MyOrganizerIssueLabeledEventDetail>>(window, MY_ORGANIZER_ISSUE_LABELED_EVENT)
                .subscribe(event => {
                    const {issueUid} = event.detail;
                    this._issues = this._issues.filter(issue => issue.uid !== issueUid);
                });

            // A ticket can also become un-labeled WITHOUT this inbox's own
            // `_onDrop` being involved at all — deleting a whole box
            // (Anke, 2026-09-09: "die Tickets, die enthalten waren in der
            // Box, wandern wieder zurück in die Inbox") clears
            // `personalLabel` on every one of its tickets from
            // `<my-organizer-label-column>`'s own delete handler, possibly
            // several at once. Exactly the `dimension="date"` UNDATED
            // listener's own reasoning below, mirrored for this dimension —
            // see its doc comment for why a full reload (not a per-uid
            // local patch) is the simplest correct reaction, and why the
            // no-op guard is needed for the (far more common) case where
            // THIS component's own `_onDrop` already fired the very same
            // event and already re-added the Issue locally.
            this.rxjs.listenUntilDisconnected<CustomEvent<MyOrganizerIssueUnlabeledEventDetail>>(window, MY_ORGANIZER_ISSUE_UNLABELED_EVENT)
                .subscribe(event => {
                    const {issueUid} = event.detail;
                    if (!this._issues.some(issue => issue.uid === issueUid)) {
                        this._reload$.next();
                    }
                });
        } else {
            // "Mein Kalender" (2026-09-07) — the date-cell analog of the
            // listener above.
            this.rxjs.listenUntilDisconnected<CustomEvent<MyOrganizerIssueDatedEventDetail>>(window, MY_ORGANIZER_ISSUE_DATED_EVENT)
                .subscribe(event => {
                    const {issueUid} = event.detail;
                    this._issues = this._issues.filter(issue => issue.uid !== issueUid);
                });

            // A ticket can also become un-dated WITHOUT this inbox's own
            // `_onDrop` being involved at all — the calendar view's own
            // stale-date reclaim (Anke, 2026-09-07: "wenn die nächste Woche
            // beginnt... dann bitte das zugeordnete persönliche Datum
            // entfernen und die Tickets wieder in der Inbox anzeigen") clears
            // `personalDate` from a component elsewhere on the page, possibly
            // for SEVERAL rows in one page load. That event only carries the
            // `issueUid`, not the full `Issue` (unlike the drop-back path
            // above, which already has the entity locally and re-inserts it
            // directly via `_insertSorted`), so a full reload is the
            // simplest correct reaction here — cheap for this small a
            // dataset. A no-op guard skips triggering a reload entirely for
            // the (far more common) case where THIS component's own
            // `_onDrop` already fired the very same event and already
            // re-added the Issue locally.
            this.rxjs.listenUntilDisconnected<CustomEvent<MyOrganizerIssueUndatedEventDetail>>(window, MY_ORGANIZER_ISSUE_UNDATED_EVENT)
                .subscribe(event => {
                    const {issueUid} = event.detail;
                    if (!this._issues.some(issue => issue.uid === issueUid)) {
                        this._reload$.next();
                    }
                });
        }

        // Clears "pressed" wherever the mouse button comes up — NOT the
        // row's own mouseup, which would be exactly as reflow-fragile as
        // native `:active` (see `_pressedIssueUid`'s own doc comment on
        // `<my-organizer-label-column>`).
        this.rxjs.listenUntilDisconnected<MouseEvent>(window, 'mouseup')
            .subscribe(() => {
                this._pressedIssueUid = null;
            });

        // The initial load — routed through `_reload$` like every other
        // trigger (review-fund, 2026-09-07, round 2), not a direct
        // `this._load()` call, so it can't race a reload the UNDATED
        // listener above starts concurrently (both pages mount together).
        this._reload$.next();
    }

    private _onRowMouseDown(issueUid: string): void {
        this._pressedIssueUid = issueUid;
    }

    private _onDragEnd(): void {
        this._draggingIssueUid = null;
    }

    /**
     * Inserts an Issue back into `_issues` at its sorted position (Anke,
     * 2026-09-06: "die inbox soll immer nach dem startdatum sortiert sein...
     * wenn ich ein issue in die inbox zurückschiebe, dann soll es sich
     * entsprechend seines startdatums einsortieren") — mirrors the ordering
     * `IssueRepository.findAllAssignedToMe()` already applies server-side
     * (`start` ascending, `title` as the tie-breaker), so a dropped-back
     * Issue lands exactly where a fresh reload would have put it, instead of
     * always at the end.
     */
    private _insertSorted(issue: Issue): void {
        const index = this._issues.findIndex(existing =>
            existing.start.toMillis() > issue.start.toMillis()
            || (existing.start.toMillis() === issue.start.toMillis() && existing.title.localeCompare(issue.title) > 0)
        );
        this._issues = index === -1
            ? [...this._issues, issue]
            : [...this._issues.slice(0, index), issue, ...this._issues.slice(index)];
    }

    /**
     * An Issue already dropped into a column of THIS page's own `dimension`
     * is excluded here too (Anke, 2026-09-06: "ein issue kann nur genau
     * einer Spalte zugewiesen werden") — not just live via the window-event
     * listener above, but also on a fresh load/page reload, when no drop
     * event has fired in this session at all. Only the ONE field `dimension`
     * names is checked — see the class doc comment on why an Issue placed
     * on the OTHER page's dimension must stay visible here.
     *
     * NOT called by any production entry point anymore (review-fund,
     * 2026-09-07, round 2) — `connectedCallback()`'s initial load, the
     * UNDATED-event reaction, and the `_onDrop` failure-fallback all push
     * onto `_reload$` instead, so every reload is correctly sequenced
     * against every other one (see `_reload$`'s own doc comment). Kept as a
     * standalone fetch-and-assign helper purely for direct, isolated test
     * calls (`component['_load']()`, used throughout this file's own test
     * suite) — correct in isolation, just not race-safe against a
     * CONCURRENT trigger, which is exactly why production code no longer
     * calls it directly.
     */
    private async _load(): Promise<void> {
        this._issues = await this._fetchIssues();
        this._loaded = true;
    }

    /**
     * The actual fetch+filter, split out from `_load()` (review-fund,
     * 2026-09-07) so the `switchMap`-routed reload above can await just the
     * DATA without also re-touching `_loaded` (already `true` by the time a
     * reload can even fire) or racing a second concurrent assignment of
     * `_issues` — see `_reload$`'s own doc comment.
     */
    private async _fetchIssues(): Promise<Issue[]> {
        const [issues, myRows] = await Promise.all([
            container.resolve(IssueRepository).findAllAssignedToMe(),
            container.resolve(IssuePersonalDataRepository).findAllMine(),
        ]);

        const placedIssueUids = new Set(
            myRows
                .filter(row => !!(this.dimension === 'label' ? row.personalLabel : row.personalDate) && !!row.issue)
                .map(row => row.issue!.uid)
        );

        return issues.filter(issue => !placedIssueUids.has(issue.uid));
    }

    /**
     * Applies a fresh `_fetchIssues()` result to `_issues` — used by the
     * `_reload$` pipeline's own `subscribe()` (NOT by `_load()`, which stays
     * a blind, dissolve-free assignment: it's only ever called for the very
     * first load, before `_issues` holds anything to diff against).
     *
     * `findAllAssignedToMe()` already excludes DONE (and REMINDER) Issues
     * SERVER-SIDE, so — unlike `<my-organizer-label-column>`/
     * `<my-organizer-calendar-day>`'s own `diffDoneRows()`, which diffs
     * against a RAW fetch that still contains the DONE row — there is no
     * "raw" list here to diff against. Instead: any currently-shown Issue
     * missing from the fresh list is re-checked directly
     * (`IssueRepository.findOneById()`, bypassing that server-side filter)
     * to see WHY it disappeared. Only a genuine DONE transition (Anke,
     * 2026-09-09, follow-up: "auch in der Inbox") gets
     * `_dissolve.start()`'d and kept a little longer; anything else (moved
     * to a label/date elsewhere, went to REMINDER, deleted) still
     * disappears immediately, exactly as before this change.
     *
     * The `missing.length === 0` fast path below (no `await` on that branch
     * at all) matters beyond just avoiding a wasted `Promise.all([])`: an
     * `async` function body runs SYNCHRONOUSLY up to its first `await`, so
     * skipping straight to `return` there keeps `_issues`'s assignment at
     * the exact same microtask depth as before this method existed (a
     * plain synchronous assignment in the `subscribe()` callback) — every
     * OTHER reload path in this file (and its tests) still assumes that.
     * Only an ACTUAL missing-and-not-already-dissolving Issue pays for the
     * extra `findOneById` round-trip.
     *
     * `generation` (review-fund, 2026-09-09, round 2) — the value
     * `subscribe()` captured from `_reloadGeneration` for THIS reload, at
     * the moment ITS fetch resolved. Checked again right before the merge,
     * AFTER the `await Promise.all(...)` below — the only place this call
     * can actually go stale: if a NEWER reload has since bumped
     * `_reloadGeneration` further, this call started resolving its own
     * `findOneById` lookups before that later reload's (faster) merge
     * already landed, and finishing now would clobber that newer state
     * with older data. `switchMap` only protects the FETCH itself from
     * this; this async tail runs independently of it. NOT checked on the
     * `missing.length === 0` fast path above — there's no `await` before
     * that branch's own `return`, and JS being single-threaded means
     * nothing else can bump `_reloadGeneration` between `subscribe()`
     * incrementing it and this method reading it moments later; a check
     * there could never observe a mismatch (review-fund, round 3 — an
     * earlier version of this comment/check suggested otherwise).
     */
    private async _applyFreshIssues(freshIssues: Issue[], generation: number): Promise<void> {
        const freshUids = new Set(freshIssues.map(issue => issue.uid));
        const missing = this._issues.filter(issue => !freshUids.has(issue.uid) && !this._dissolve.has(issue.uid));

        if (missing.length === 0) {
            this._mergeFreshIssues(freshIssues);
            return;
        }

        const missingWithFreshCopy = await Promise.all(missing.map(async issue => ({
            issue,
            fresh: await container.resolve(IssueRepository).findOneById(issue.uid),
        })));

        if (generation !== this._reloadGeneration) {
            return; // superseded while the `findOneById` lookups were in flight.
        }

        // Fresh copies of the just-started-dissolving Issues (review-fund,
        // 2026-09-09, round 3): `freshIssues` itself EXCLUDES them (the
        // server-side DONE filter), so without this map `_mergeFreshIssues`
        // would keep showing each one with its OLD (pre-DONE) status for
        // the entire "kurz sehen" pause and animation — contradicting
        // `DissolveFlashController`'s own doc comment ("showing its new
        // 'Done' status for a beat") and inconsistent with `<my-organizer-
        // label-column>`/`<my-organizer-calendar-day>`'s own `diffDoneRows`,
        // which always shows the fresh reference.
        const dissolvingFreshByUid = new Map<string, Issue>();
        for (const {issue, fresh} of missingWithFreshCopy) {
            if (fresh && IssueHelper.isDone(fresh)) {
                dissolvingFreshByUid.set(issue.uid, fresh);
                this._dissolve.start(issue.uid, () => {
                    this._issues = this._issues.filter(i => i.uid !== issue.uid);
                });
            }
        }

        this._mergeFreshIssues(freshIssues, dissolvingFreshByUid);
    }

    /**
     * Merges `freshIssues` into `_issues` PRESERVING each still-shown
     * Issue's current position (Anke, 2026-09-09, bug report: a row must
     * not jump to the end of the list the moment it starts dissolving —
     * the first version of this method rebuilt `_issues` as `[...fresh,
     * ...dissolving]`, which is exactly what silently relocated it).
     * Walks `_issues`'s own order first — carrying over anything still
     * fresh OR still mid-dissolve (using the FRESH reference where
     * available, so e.g. a title edit still shows up live; for a
     * dissolving Issue that `freshIssues` itself doesn't contain — the
     * server-side DONE filter excludes it — falling back to
     * `dissolvingFreshByUid`'s own fresh-but-DONE copy instead, so the row
     * shows its actual new status while it dissolves, review-fund,
     * 2026-09-09, round 3; only truly falling back to the stale `issue`
     * reference if neither has one) — then appends whatever's brand new in
     * `freshIssues` at the end, exactly like an arriving Issue always has.
     */
    private _mergeFreshIssues(freshIssues: Issue[], dissolvingFreshByUid: ReadonlyMap<string, Issue> = new Map()): void {
        const freshByUid = new Map(freshIssues.map(issue => [issue.uid, issue]));
        const currentUids = new Set(this._issues.map(issue => issue.uid));

        const carriedOver = this._issues
            .filter(issue => freshByUid.has(issue.uid) || this._dissolve.has(issue.uid))
            .map(issue => freshByUid.get(issue.uid) ?? dissolvingFreshByUid.get(issue.uid) ?? issue);

        const brandNew = freshIssues.filter(issue => !currentUids.has(issue.uid));

        this._issues = [...carriedOver, ...brandNew];
    }

    /**
     * "+ Aufgabe" (Anke, 2026-09-17) — opens the standard Issue create
     * dialog, same as `<ha-tasklist-view>`'s own "+" button
     * (`OpenNewIssueDialogCommandHandler`), but pre-assigns the new Issue to
     * the CURRENT employee first so it actually lands in THIS column —
     * `findAllAssignedToMe()` (the query this column loads from) requires a
     * matching `EmployeeToIssueAssignment` row, which a bare
     * `IssueRepository.create()` does not add on its own.
     *
     * Pre-setting `template.employeeToIssueAssignments` before opening a
     * `creationMode: true` dialog is the SAME pattern
     * `abstract-standard-timeline.view.ts`'s own `_onAddIssueButtonClick`
     * already uses to pre-assign a Y-axis employee — `Issue.clone()` (which
     * `AbstractEntitiesDialogComponent.createNewEntity()` runs on this
     * template) re-derives a fresh assignment row from it, so it survives
     * into what actually gets saved. Nothing is persisted here directly —
     * both `IssueRepository.create()` and
     * `EmployeeToIssueAssignmentRepository.create()` only build in-memory,
     * `EntityState.NEW` entities; the real backend command only fires once
     * the user clicks Save inside the opened dialog.
     */
    private _onCreateTask = async (): Promise<void> => {
        if (this._creating) {
            return;
        }
        this._creating = true;
        try {
            const template = await container.resolve(IssueRepository).create();
            const me = await appState.employee;
            if (me) {
                const assignment = await container.resolve(EmployeeToIssueAssignmentRepository).create(template, me);
                if (assignment) {
                    template.employeeToIssueAssignments = [assignment];
                }
            } else {
                // Nothing to pre-assign to, so the saved task would never
                // show up in THIS column (`findAllAssignedToMe`) — say so
                // instead of leaving the User wondering where it went.
                UiHelper.notify({
                    level: 'info',
                    title: 'Aufgabe ohne Zuweisung',
                    content: html`<p>Ihrem Benutzer ist kein Mitarbeiter zugeordnet — die neue Aufgabe wird
                        keinem Mitarbeiter zugewiesen und erscheint deshalb nicht in Ihrer Inbox.</p>`,
                    closable: true,
                });
            }
            UiHelper.openDialogEntityEdit(Issue, template, {creationMode: true, closable: true});
        } catch (error) {
            console.error(`Failed to open the new-task dialog.`, error);
            UiHelper.notify({
                level: 'error',
                title: 'Fehler',
                content: html`<p>Der Dialog für die neue Aufgabe konnte nicht geöffnet werden.</p>`,
                closable: true,
            });
        } finally {
            this._creating = false;
        }
    };

    /**
     * `repeat()` with `issue.uid` as the key, NOT a plain `.map()` — see
     * `<my-organizer-label-column>`'s own doc comment on its `render()` for
     * the full reasoning (native/CSS state like `:active` or an in-flight
     * `--just-moved-fading` transition otherwise sticks to a reused DOM
     * position instead of following its own Issue). This column doesn't
     * reorder its OWN list today, but it shares the exact same
     * `--just-moved`/`--just-moved-fading` classes, and a future Milestone
     * exclusion/re-sort could reorder it without anyone remembering to
     * revisit this — cheap enough to apply defensively now.
     */
    public render(): HTMLTemplateResult {
        if (!this._loaded) {
            return html``;
        }

        return html`
            <div class="my-organizer-assigned-column ${this._isDropTarget ? 'my-organizer-assigned-column--drop-target' : ''}"
                 @dragover=${this._onDragOver}
                 @dragleave=${this._onDragLeave}
                 @drop=${this._onDrop}>
                <div class="my-organizer-assigned-column__header">
                    ${translate('personal-organizer.assigned-column.title')}
                    <button type="button"
                            title=${translate('personal-organizer.assigned-column.add-task')}
                            class="my-organizer-add-task add-entity buttonform-line"
                            data-label=${translate('personal-organizer.assigned-column.add-task')}
                            @click=${this._onCreateTask}>
                        <div class="hover-effect"></div>
                    </button>
                </div>
                <div class="my-organizer-assigned-column__body">
                    ${this._issues.length === 0
                        ? html`
                            <div class="my-organizer-assigned-column__empty-state">
                                ${translate('personal-organizer.assigned-column.empty-state')}
                            </div>
                        `
                        : repeat(this._issues, issue => issue.uid, issue => this._renderRow(issue))}
                </div>
            </div>
        `;
    }

    private _renderRow(issue: Issue): HTMLTemplateResult {
        return html`
            <div class=${classMap({
                'my-organizer-assigned-column__row': true,
                'my-organizer-assigned-column__row--pressed': this._pressedIssueUid === issue.uid,
                'my-organizer-assigned-column__row--dragging': this._draggingIssueUid === issue.uid,
                'my-organizer-assigned-column__row--just-moved': this._justMoved.isJustMoved(issue.uid),
                'my-organizer-assigned-column__row--just-moved-fading': this._justMoved.isFading(issue.uid),
                'my-organizer-assigned-column__row--dissolving': this._dissolve.isDissolving(issue.uid),
            })}
                 draggable="true"
                 @mousedown=${() => this._onRowMouseDown(issue.uid)}
                 @dragstart=${(event: DragEvent) => this._onDragStart(event, issue)}
                 @dragend=${this._onDragEnd}
                 @click=${() => this._onEditIssue(issue)}
                 data-root-type=${rootUidOf(issue.type) ?? nothing}>
                ${issue.status ? html`<div class="my-organizer-assigned-column__status"
                     style=${styleMap({"--item-color": issue.status.color})}>${issue.status.key}</div>` : nothing}
                <div class="my-organizer-assigned-column__date">${issue.start.toLocaleString(DATE_FORMAT)}</div>
                <div class="my-organizer-assigned-column__title">${issue.title != '' ? issue.title : '-'}</div>
            </div>
        `;
    }

    /**
     * Opens the standard Issue-edit dialog (Anke, 2026-09-06: "auf Klick auf
     * ein Issue den Standard Issue-Edit-Dialog öffnen") — the exact same
     * static helper `<ha-task-list-item>`'s own row click uses
     * (`IssueHelper.openIssueEditDialog`), so behavior/edit-permissions/
     * dedup-if-already-open all match the rest of the app; no new dialog
     * plumbing needed here. A plain `click` coexists fine with the row's own
     * `draggable`/`dragstart` — a native drag gesture (pointer moves past the
     * threshold before release) never fires a `click` on drop, only an
     * actual, no-movement click does.
     */
    private _onEditIssue(issue: Issue): void {
        void IssueHelper.openIssueEditDialog(issue);
    }

    /**
     * `dataTransfer.setData` is set too (not just the shared drag-context)
     * so the browser shows a drag affordance and a drop is possible at all —
     * a `dragstart` with no data set is treated by some browsers as "nothing
     * to drag". The actual payload a drop handler reads is the live `Issue`
     * off `MyOrganizerDragContext` (see its own doc comment).
     */
    private _onDragStart(event: DragEvent, issue: Issue): void {
        MyOrganizerDragContext.draggedIssue = issue;
        this._pressedIssueUid = null; // --dragging (same color) takes over from here.
        this._draggingIssueUid = issue.uid;
        event.dataTransfer?.setData('text/plain', issue.uid);
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
        }
    }

    private _onDragOver(event: DragEvent): void {
        // Native HTML5 DnD refuses a drop on any element unless dragover is
        // explicitly prevented — without this, `drop` never fires at all
        // (mirrors `<my-organizer-label-column>`'s own handler).
        event.preventDefault();
        this._isDropTarget = true;
    }

    private _onDragLeave(): void {
        this._isDropTarget = false;
    }

    /**
     * The reverse of `<my-organizer-label-column>`'s (`dimension="label"`)
     * or `<my-organizer-calendar-day>`'s (`dimension="date"`) own drop
     * handler (Anke, 2026-09-06/2026-09-07): dragging an Issue back out of
     * a column of THIS page's own dimension onto the inbox clears ONLY that
     * one field — `personalLabel` on "Meine Boxen", `personalDate` on "Mein
     * Kalender" — never the other one, mirroring `_load`'s own single-field
     * exclusion (see the class doc comment). The row is expected to already
     * have an `IssuePersonalData` row (it must have had one to be shown in
     * a column in the first place) — if for some reason it doesn't, there
     * is nothing to clear and thus nothing to do.
     */
    private async _onDrop(event: DragEvent): Promise<void> {
        event.preventDefault();
        this._isDropTarget = false;

        const issue = MyOrganizerDragContext.draggedIssue;
        MyOrganizerDragContext.draggedIssue = null;
        if (!issue) {
            return;
        }

        // Already shown here (e.g. dropped back onto its own column) —
        // nothing to persist.
        if (this._issues.some(existing => existing.uid === issue.uid)) {
            return;
        }

        const repository = container.resolve(IssuePersonalDataRepository);
        const existingRow = await repository.findOneByIssue(issue.uid);
        if (!existingRow) {
            return;
        }

        const commandBus = container.resolve(CommandBus);
        let dispatched: boolean;

        if (this.dimension === 'label') {
            if (!existingRow.personalLabel) {
                return; // nothing of THIS dimension to clear.
            }
            existingRow.personalLabel = null;
            dispatched = await commandBus.dispatchCommand(
                new UnassignIssueFromPersonalLabelCommand('issue-personal-data', existingRow.uid, existingRow)
            );
            if (dispatched) {
                window.dispatchEvent(new CustomEvent<MyOrganizerIssueUnlabeledEventDetail>(
                    MY_ORGANIZER_ISSUE_UNLABELED_EVENT,
                    {detail: {issueUid: issue.uid}}
                ));
            } else {
                console.error('[my-organizer-assigned-column] failed to clear the Issue\'s personal label');
            }
        } else {
            if (!existingRow.personalDate) {
                return;
            }
            existingRow.personalDate = null;
            dispatched = await commandBus.dispatchCommand(
                new UpdateIssuePersonalDataCommand('issue-personal-data', existingRow.uid, existingRow)
            );
            if (dispatched) {
                window.dispatchEvent(new CustomEvent<MyOrganizerIssueUndatedEventDetail>(
                    MY_ORGANIZER_ISSUE_UNDATED_EVENT,
                    {detail: {issueUid: issue.uid}}
                ));
            } else {
                console.error('[my-organizer-assigned-column] failed to clear the Issue\'s personal date');
            }
        }

        // The success/error TOAST itself is NOT triggered here — see
        // `MyOrganizerNotificationService` (ADR-014: notifications react to
        // the CommandBus/EventBus business event, decoupled from this drop
        // handler).
        if (dispatched) {
            this._insertSorted(issue);
            this._justMoved.flash(issue.uid);
        } else {
            // Routed through `_reload$` like every other trigger
            // (review-fund, 2026-09-07, round 2) instead of a direct
            // `await this._load()` — same reasoning as the initial load in
            // `connectedCallback()`.
            this._reload$.next();
        }
    }

}
